import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { getEntityId, showApiErrors } from '@/utils';
import { AdminsListState, ADMINS_LIST_SLICE_NAME, initialState } from './models';
import {
  getAdminDetails,
  updateAdminDetails,
  createAdmin,
  getAdminsList,
  deleteAdmin,
  activateAdmin,
  deactivateAdmin,
} from './actionCreators';
import { TAdminFiltersValues } from '../helpers/types';
import { AdminStatus } from '@/models/admins.model';

export const slice = createSlice({
  name: ADMINS_LIST_SLICE_NAME,
  initialState,
  reducers: {
    resetAdminsListState() {
      return { ...initialState };
    },
    updateAdminFilters: (state: AdminsListState, action: PayloadAction<TAdminFiltersValues>) => {
      state.adminFilters = { ...(state.adminFilters ?? {}), ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminDetails.fulfilled, (state: AdminsListState, action) => {
        state.adminDetails = action.payload;
        state.isLoading = false;
      })
      .addCase(getAdminsList.fulfilled, (state: AdminsListState, action) => {
        state.adminsList = action.payload.items;
        state.isLoading = false;
        state.pagination = {
          limit: action.meta.arg.limit,
          ...action.payload.cursor,
        };
      })
      .addCase(deleteAdmin.fulfilled, (state: AdminsListState) => {
        state.isLoading = false;
      })
      .addCase(activateAdmin.fulfilled, (state: AdminsListState, action) => {
        const updatedList = (state.adminsList ?? []).map((admin) =>
          getEntityId(admin.PK) === action.meta.arg ? { ...admin, status: AdminStatus.ACTIVE } : admin,
        );
        state.adminsList = updatedList;
        state.isLoading = false;
      })
      .addCase(deactivateAdmin.fulfilled, (state: AdminsListState, action) => {
        const updatedList = (state.adminsList ?? []).map((admin) => {
          return getEntityId(admin.PK) === action.meta.arg ? { ...admin, status: AdminStatus.DEACTIVATED } : admin;
        });
        state.adminsList = updatedList;
        state.isLoading = false;
      })
      .addMatcher(isPending(updateAdminDetails, createAdmin, deleteAdmin, getAdminsList), (state: AdminsListState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled(updateAdminDetails, createAdmin), (state: AdminsListState, action) => {
        state.adminDetails = action.payload;
        state.isLoading = false;
      })
      .addMatcher(isRejected(updateAdminDetails, createAdmin, deleteAdmin), (state: AdminsListState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;

        showApiErrors(error);
      });
  },
});

export const { resetAdminsListState, updateAdminFilters } = slice.actions;

export default slice.reducer;
